import React, { useState, useContext } from 'react';
import { format } from 'date-fns';

import './BusDetailsList.css';
import { getAppEnv } from '../../../config/env';
import { BusGroup, Bus } from '../../pages/runtime-typechecker';
import MySafetyTag from '../../components/MySafetyTag/MySafetyTag';
import { operatorShortNameMap } from './operatorShortNameMap';
import { Context } from '../../../src/AppContext';
import { getDifferenceInDays } from '../../utils/timeUtils';
import { generateUuid } from '../../utils/uuidGenerator';
import axios from 'axios';
import { yieldToMainThread } from '../../utils/scheduler';

const env = getAppEnv();
const { PUBLIC_URL } = env.raw;

function textTruncate(
  str,
  length: number | null = null,
  ending: string | null = null
) {
  if (length == null) {
    length = 35;
  }
  if (ending == null) {
    ending = '...';
  }
  if (str.length > length) {
    return str.substring(0, length - ending.length) + ending;
  } else {
    return str;
  }
}
const isSSR = () => typeof window === 'undefined';
type BusDetailsListProps = {
  type: string;
  titlePrefix: string;
  noOfListings: number;
  busGroups: BusGroup[];
  route: string;
  setWidget: React.Dispatch<React.SetStateAction<number | null>>;
  setcollapsableFlag: React.Dispatch<React.SetStateAction<boolean>>;
  onBusClicked: (funnelUrl?: string | undefined) => void;
  isMobileDevice?: boolean;
  fromCity?: string;
  toCity?: string;
  fromCode?: string;
  toCode?: string;
  selectedDay: Date;
  operatorName?: string;
};
type Buses = typeof Bus[];

const BusDetailsList = ({
  type,
  titlePrefix,
  noOfListings,
  busGroups,
  route,
  // setWidget,
  isMobileDevice,
  setcollapsableFlag,
  onBusClicked,
  fromCity,
  toCity,
  fromCode,
  toCode,
  selectedDay,
  operatorName
}: BusDetailsListProps) => {
  const { trackTemplateEvent, omniUrlArgs } = useContext(Context);

  const [selectedBusType, setSelectedBusType] = useState<null | string>(
    busGroups.length > 0 ? busGroups[0].groupName : null
  );

  const busClick = async (
    idx: number,
    funnelUrl?: string | null,
    departureDate?: string | null
  ) => {
    const journeyDate = format(selectedDay, 'dd-MM-yyyy');
    let updatedFunnelUrl: string | undefined = '';
    if (departureDate) {
      updatedFunnelUrl = funnelUrl?.replace(departureDate, journeyDate);
      if (isMobileDevice) {
        updatedFunnelUrl = updatedFunnelUrl?.replace('deptDate', journeyDate);
      }
    }
    trackTemplateEvent(`Bus_SEO${type}_SelectSeats_Click`);
    funnelUrl && onBusClicked(funnelUrl);
    setcollapsableFlag(false);

    if (operatorName && idx === 3) {
      const filterOption = {
        tag: 'Operator',
        value: operatorName
      };

      const requestBody = {
        type: 'UserSessionSelectionRequest',
        seo: {
          pageName: 'Operator',
          filters: [filterOption]
        }
      };

      const sessionId = generateUuid();

      const headers = {
        sessionId
      };

      const endPoint = 'https://bus.makemytrip.com/bus/postFilterContext';
      try {
        const response = await axios.post(endPoint, requestBody, { headers });
      } catch (error) {
        // Handle the error
        console.error(error);
      }

      const urlPrefix = isMobileDevice
        ? `https://www.makemytrip.com/mbus/rnw/busListingPage?from=${fromCity}&from_code=${fromCode}&to=${toCity}&to_code=${toCode}&departure=${journeyDate}&sort=Rating&`
        : `https://www.makemytrip.com/bus/search/${fromCity}/${toCity}/travelDate?from_city=${fromCode}&to_city=${toCode}&`;

      const urlSuffix = `${omniUrlArgs}&sessionId=${sessionId}`;
      const url = urlPrefix + urlSuffix;

      if (isMobileDevice) {
        return (window.location = Object(url));
      } else {
        const newTab = window.open(url, '_blank');
        return newTab?.focus();
      }
    }

    if (updatedFunnelUrl) {
      const url = `${updatedFunnelUrl}&${omniUrlArgs}`;
      if (isMobileDevice) {
        return (window.location = Object(url));
      } else {
        const newTab = window.open(url, '_blank');
        return newTab?.focus();
      }
    } else {
      const url = isMobileDevice
        ? `https://www.makemytrip.com/mbus/rnw/busListingPage?from=${fromCity}&from_code=${fromCode}&to=${toCity}&to_code=${toCode}&departure=${journeyDate}&sort=Rating&` +
          omniUrlArgs
        : `https://www.makemytrip.com/bus/search/${fromCity}/${toCity}/travelDate?from_city=${fromCode}&to_city=${toCode}&` +
          omniUrlArgs;

      if (isMobileDevice) {
        return (window.location = Object(url));
      } else {
        const newTab = window.open(url, '_blank');
        return newTab?.focus();
      }
    }
  };

  const yieldBusClick = async (
    idx: number,
    funnelUrl?: string | null,
    departureDate?: string | null
  ) => {
    await yieldToMainThread(() => {
      requestAnimationFrame(() => {
        busClick(idx, funnelUrl, departureDate);
      });
    });
  };

  const handleBusSelect = busType => {
    setSelectedBusType(busType);
    trackTemplateEvent(`Bus_SEO${type}_${busType}_Click`);
  };

  const getBusGropuWithSelectedType = (selectedBusType): Bus[] => {
    let matchedBusGroup: Bus[] = [];
    busGroups.forEach((busGroup: BusGroup) => {
      if (busGroup['groupName'] === selectedBusType) {
        matchedBusGroup = busGroup['buses'];
      }
    });
    return matchedBusGroup;
  };

  const getFormattedTime = (timeStr: string) => {
    return timeStr
      .replace(' ', ` <span class='font12 appendLeft3 appendBottom1'>`)
      .concat(`</span>`);
  };
  const getFormattedDuration = (duration: number) => {
    const hrs = Math.floor(duration / 60);
    const mins = duration % 60;
    return mins ? `${hrs}h ${mins}m` : `${hrs}h`;
  };
  const getFormattedTimeMobile = (timeStr: string) => {
    const hrs = Number(timeStr.substr(0, 2));
    if (timeStr.includes(' AM')) {
      if (hrs == 12) {
        return ('00' + timeStr.substr(2)).replace(' AM', '');
      }
      return timeStr.replace(' AM', '');
    } else if (timeStr.includes(' PM')) {
      if (hrs == 12) {
        return (hrs.toString() + timeStr.substr(2)).replace(' PM', '');
      }
      return ((hrs + 12).toString() + timeStr.substr(2)).replace(' PM', '');
    }
  };

  return (
    <>
      <section className={`desktopShow`}>
        <div className="cabTypeWrap">
          <div className="tapWrap">
            <div className="tabTypeTitle">
              {type == '_Route_Operator' ||
              type == '_Route_Category' ||
              type == '_Route' ? (
                <h2>
                  <span style={{ display: 'block' }} className="headingText">
                    {titlePrefix}
                  </span>
                  <span className="subHeadingText">{route}</span>
                </h2>
              ) : (
                <>
                  <p className="headingText">{titlePrefix}</p>
                  <p className="subHeadingText">{route}</p>
                </>
              )}
            </div>
            {/* {type==='_Route_Operator' && busGroups.length==1 ?
            null
            : */}
            <ul className="cabTap">
              {busGroups.map(busType => {
                const operatorShortName =
                  operatorShortNameMap[busType.groupName];
                return (
                  <li
                    onClick={() => handleBusSelect(busType.groupName)}
                    key={busType.groupName}
                    className={
                      selectedBusType === busType.groupName ? 'active' : ''
                    }
                  >
                    <span className="activeCheck"></span>
                    <span>
                      {busType.groupImageUrl == null &&
                      operatorShortName == 'RSRTC' ? (
                        <img
                          data-src={
                            'https://imgak.mmtcdn.com/bus_cdn_dt/static/img/rsrtc_logo.png'
                          }
                          height={36}
                          width={36}
                          alt="operator image"
                          className="lazyload"
                        />
                      ) : busType.groupImageUrl ? (
                        <img
                          data-src={busType.groupImageUrl}
                          height={36}
                          width={36}
                          alt="operator image"
                          className="lazyload"
                        />
                      ) : (
                        <span className={`agencyLogoWrap`}>
                          {' '}
                          {String(busType.groupName)
                            .charAt(0)
                            .toUpperCase()}{' '}
                        </span>
                      )}
                    </span>
                    <span
                      style={{ width: 'auto', minWidth: '112px' }}
                      className="cabTabtext"
                    >
                      {operatorShortName
                        ? operatorShortName
                        : busType.groupName}
                    </span>
                    <span className="circleOuter">
                      <span className="circleInner"></span>
                    </span>
                  </li>
                );
              })}
            </ul>
            {/* } */}
          </div>
        </div>
        <table style={{ width: '100%' }} className="busDtLst">
          <thead className={`makeBlock`}>
            <tr>
              <th>
                <span>Bus Details</span>
              </th>
              <th>
                <span>Time and Duration</span>
              </th>
              <th>
                <span>Rating</span>
              </th>
              <th>
                <span>Price</span>
              </th>
            </tr>
          </thead>
          <tbody className="makeBlock">
            {selectedBusType &&
              getBusGropuWithSelectedType(selectedBusType)
                // .filter((_, index) => showFullBuses || index < noOfListings)
                .filter((_, index) => index < noOfListings)
                .map((item, index) => {
                  return (
                    <tr key={index}>
                      <td className="busDtLst__busNmInfo">
                        {
                          // item.desktopUrl
                          //   ?
                          //   <a href={item.desktopUrl.replace(/deptDate/g, format(new Date, 'dd-MM-yyyy'))} className="busDtLst__busNmInfo--busName">{textTruncate(item.operatorName)} </a>
                          //   :
                          <span className="busDtLst__busNmInfo--busName textLeft">
                            {textTruncate(item.operatorName)}
                          </span>
                        }
                        <span
                          className="busDtLst__busNmInfo--busInfo"
                          style={{
                            marginBottom: `${
                              item.safetyPlusEnabled ? '0px' : '22px'
                            }`
                          }}
                        >
                          {item.busType}
                        </span>
                        {item.safetyPlusEnabled && <MySafetyTag />}
                        <span className="busDtLst__busNmInfo--busSeats">
                          <span>
                            <img
                              width={24}
                              height={24}
                              data-src={`${PUBLIC_URL}/static/public/ic-seater-color.png`}
                              alt={'available seats'}
                              className="lazyload"
                            />
                          </span>
                          <span className="appendRight16">{` ${item.availableSeats} Seats`}</span>
                          <span>{`${item.avlWindowSeats} Window Seats`}</span>
                        </span>
                      </td>
                      <td className="busDtLst__timings">
                        <span
                          className="font22 latoBold blackText appendRight20 makeFlex end"
                          dangerouslySetInnerHTML={{
                            __html: getFormattedTime(item.departureTimeStr)
                          }}
                        ></span>
                        <span className="busDtLst__timings--tt">
                          <span className="busDtLst__timings--tt-txt">
                            {getFormattedDuration(item.durationInMins)}
                          </span>
                        </span>
                        <span
                          className="font22 latoBold blackText appendLeft20 makeFlex end "
                          dangerouslySetInnerHTML={{
                            __html: getFormattedTime(item.arrivalTimeStr)
                          }}
                        ></span>
                      </td>
                      <td className="busDtLst__rating">
                        {item.rating && (
                          <span className="busDtLst__rating--bx">
                            {item.rating}
                          </span>
                        )}
                        <span
                          className={`font12 `}
                          style={{
                            paddingTop: `${item.rating ? '0px' : '9px'}`
                          }}
                        >
                          {item.noOfRatings
                            ? `${item.noOfRatings} ratings`
                            : null}
                        </span>
                      </td>
                      <td className="busDtLst__busPrice">
                        <span
                          className="busDtLst__busPrice--ttp"
                          style={{
                            marginBottom: `${
                              item.safetyPlusEnabled ? '40px' : '24px'
                            }`
                          }}
                        >
                          <span>{`₹ ${Math.round(item.minFare)}`}</span>
                          <span className="appendLeft25 font10 latoRegular darkGreyText appendTop8">
                            onwards
                          </span>
                        </span>
                        <a
                          className="bluePrimarybtn"
                          onClick={() =>
                            yieldBusClick(
                              2,
                              item.desktopUrl,
                              item.departureDate
                            )
                          }
                        >
                          Select seats
                        </a>
                      </td>
                    </tr>
                  );
                })}
          </tbody>
        </table>
        {/* {getBusGropuWithSelectedType(selectedBusType).length > noOfListings && (
          <div className="showMoreBusesRoute">
            <button
              className="showMoreBusesButtonRoute"
              onClick={() => setShowFullBuses(!showFullBuses)}
            >
              {showFullBuses ? 'VIEW LESS' : 'VIEW MORE'}
            </button>
          </div>
        )}
         */}
        {
          <div className="showMoreBusesRoute">
            <button
              className="showMoreBusesButtonRoute"
              onClick={() => {
                trackTemplateEvent(`ViewMore_Click`);
                yieldBusClick(3);
              }}
            >
              {'VIEW MORE'}
            </button>
          </div>
        }
      </section>
      <section>
        <div
          style={{ display: 'none' }} // disabled in SSR HTM
          className="cabTypeWrap busTypeWrap mobileShow"
        >
          <div className="tapWrap">
            {type == '_Route_Operator' ||
            type == '_Route_Category' ||
            type == '_Route' ? (
              <h2>
                <span
                  style={{ display: 'block' }}
                  className="font22 latoLight appendBottom2"
                >
                  {titlePrefix}
                </span>
                <span
                  className={`font16 latoBold lineHeight20 blackText ${
                    busGroups && busGroups.length > 1 ? `appendBottom20` : ``
                  }`}
                >
                  {route}
                </span>
              </h2>
            ) : (
              <>
                <p className="font22 latoLight appendBottom2">{titlePrefix}</p>
                <p
                  className={`font16 latoBold lineHeight20 blackText ${
                    busGroups && busGroups.length > 1 ? `appendBottom20` : ``
                  }`}
                >
                  {route}
                </p>
              </>
            )}
            {busGroups && busGroups.length > 1 && (
              <ul className="cabTap">
                {busGroups.map((busType, idx) => {
                  const operatorShortName =
                    operatorShortNameMap[busType.groupName];
                  return (
                    <li
                      onClick={() => handleBusSelect(busType.groupName)}
                      className={
                        selectedBusType === busType.groupName ? 'active' : ''
                      }
                      key={busType.groupName}
                    >
                      <span className="activeCheck"></span>
                      <span>
                        {/* className={`cabSeoSprite ${getCabTypeIcon(
                        busType.groupImageUrl
                      )}`}
                    > */}
                        {busType.groupImageUrl == null &&
                        operatorShortName == 'RSRTC' ? (
                          <img
                            data-src={
                              'https://imgak.mmtcdn.com/bus_cdn_dt/static/img/rsrtc_logo.png'
                            }
                            height={36}
                            width={36}
                            alt="operator image"
                            className="lazyload"
                          />
                        ) : busType.groupImageUrl ? (
                          <img
                            data-src={busType.groupImageUrl}
                            height={36}
                            width={36}
                            alt="operator image"
                            className="lazyload"
                          />
                        ) : (
                          <span className={`agencyLogoWrap`}>
                            {' '}
                            {String(busType.groupName)
                              .charAt(0)
                              .toUpperCase()}{' '}
                          </span>
                        )}
                      </span>
                      {/* <span className="cabTabtext">{busType.type}</span>
                    <span className="circleOuter">
                      <span className="circleInner"></span>
                    </span> */}
                      <span
                        style={{ width: 'auto', minWidth: '112px' }}
                        className={`cabTabtext${
                          selectedBusType === busType.groupName ? `Active` : ``
                        }`}
                      >
                        {operatorShortName
                          ? operatorShortName
                          : busType.groupName}
                      </span>
                      <span className="circleOuter">
                        <span className="circleInner"></span>
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <div className="busInfoTplCont">
            {selectedBusType &&
              getBusGropuWithSelectedType(selectedBusType)
                // .filter((_, index) => showFullBuses || index < noOfListings)
                .filter((_, index) => index < noOfListings)
                .map((item, index) => (
                  <div
                    className="busInfoTpl"
                    key={index}
                    onClick={() =>
                      yieldBusClick(2, item.mwebUrl, item.departureDate)
                    }
                  >
                    <div className="busInfoTpl__left">
                      <p className="busInfoTpl__left--title">
                        {/* {textTruncate(item.operatorName)} */}
                        {
                          // item.desktopUrl
                          //   ?
                          //   <a href={item.desktopUrl.replace(/deptDate/g, format(new Date, 'dd-MM-yyyy'))} className="busDtLst__busNmInfo--busName">{textTruncate(item.operatorName)} </a>
                          //   :
                          <span className="busDtLst__busNmInfo--busName">
                            {textTruncate(item.operatorName)}
                          </span>
                        }
                      </p>
                      <p className="font12 lightGreyText appendBottom10">
                        {item.busType}
                      </p>
                      {item.safetyPlusEnabled && <MySafetyTag />}
                      <p className="appendBottom5">
                        <span className="font16 blackText latoBlack appendRight3">
                          {getFormattedTimeMobile(item.departureTimeStr)}
                        </span>
                        <span className="font12 latoMedium lightGreyText appendRight3">
                          {`- ${getFormattedDuration(item.durationInMins)} -`}
                        </span>
                        <span className="font16 blackText latoBlack appendRight3 makeRelative">
                          {getFormattedTimeMobile(item.arrivalTimeStr)}
                          {(() => {
                            const dateDiff = getDifferenceInDays(
                              item.departureDate,
                              item.arrivalDate
                            );
                            return dateDiff ? (
                              <span className="dayLabel">+{dateDiff} day</span>
                            ) : null;
                          })()}
                        </span>
                      </p>
                      <p>
                        <span className="font10 appendRight8">{` ${item.availableSeats} Seats`}</span>
                        <span className="font10">{`${item.avlWindowSeats} Window Seats`}</span>
                      </p>
                    </div>
                    <div className="busInfoTpl__right">
                      {item.rating ? (
                        <span className="ratingLabel">
                          <>
                            <span className="latoBlack font12">
                              {item.rating}
                            </span>
                            <span className="latoBlack">/</span>5
                          </>
                        </span>
                      ) : (
                        <span className="nullRating">&nbsp;</span>
                      )}
                      <span
                        className="font10 lightGreyText appendBottom10 paddingT10"
                        style={{ marginLeft: 'auto' }}
                      >
                        {item.noOfRatings
                          ? `${item.noOfRatings} ratings`
                          : null}
                      </span>
                      <span
                        className="font16 latoBlack blackText appendBottom8"
                        style={{
                          marginLeft: 'auto',
                          marginTop: 'auto',
                          marginBottom: '22px'
                        }}
                      >
                        {`₹ ${item.minFare}`}
                      </span>
                      <span className="font12 lightGreyText lineThrough"></span>
                    </div>
                  </div>
                ))}
          </div>
          {/* {getBusGropuWithSelectedType(selectedBusType).length >
            noOfListings && (
              <div className="btnCont">
                <a
                  className="bluePrimarybtn"
                  onClick={() => setShowFullBuses(!showFullBuses)}
                >
                  {showFullBuses ? 'VIEW LESS' : 'VIEW MORE'}
                </a>
              </div>
            )} */}
          <div className="btnCont">
            <a
              className="bluePrimarybtn"
              onClick={() => {
                trackTemplateEvent(`ViewMore_Click`);
                yieldBusClick(3);
              }}
            >
              {'VIEW MORE'}
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default BusDetailsList;
